import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Grid, GridColumn, Icon, Input, Modal } from "semantic-ui-react";

import { Dropdown } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import "modules/ChecklistsDashboard/components/AddQuestionModal/AddQuestionModal.scss";
import { QuestionsColumn } from "modules/ChecklistsDashboard/components/AddQuestionModal/components";
import { useDealerQuestionsQuery, useDefaultQuestionsQuery, useTagsQuery } from "modules/ChecklistsDashboard/hooks";
import { hasTyreElement } from "modules/ChecklistsDashboard/util";
import { isAdmin } from "utils/common";

import type { Checklist, Question, QuestionGroup, TagID } from "models";

type AddQuestionModalProps = {
  isOpen: boolean;
  questionGroup?: QuestionGroup;
  onSelectQuestion: (question: Question) => void;
  onClose: () => void;
};

const FormHelper = createFormHelper<Checklist>();

export const AddQuestionModal = ({ isOpen, questionGroup, onSelectQuestion, onClose }: AddQuestionModalProps) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTagId, setSelectedTagId] = useState<TagID>();

  const dealerId = FormHelper.useWatch("dealer_id");
  const questionGroups = FormHelper.useWatch("question_groups");
  const parentChecklist = FormHelper.useWatch("parent_checklist");
  const { user } = usePermanentRealTimeUser();
  const { data: tagsOptions = [] } = useTagsQuery();
  const { data: dealerQuestions = [] } = useDealerQuestionsQuery();
  const { data: defaultQuestions = [] } = useDefaultQuestionsQuery();

  const isAdminUser = isAdmin(user?.role_id ?? 0);

  const matchesFilters = (question: Question) =>
    (question.title ?? "").toLowerCase().includes(searchTerm.toLowerCase()) && (!selectedTagId || question.tags?.some(tag => tag.id === selectedTagId));

  const getAddQuestionError = (question: Question) => {
    if (!hasTyreElement(question)) return;
    if (questionGroup?.questions?.length) return t("v8_cannot_add_tyre_question_group_not_empty", "Cannot add a tyre question because this group is not empty");

    const checklistQuestionGroups = [...(questionGroups ?? []), ...(parentChecklist?.question_groups ?? [])];
    if (checklistQuestionGroups.some(qg => qg.questions?.some(hasTyreElement)))
      return t(
        "v8_cannot_add_tyre_question_already_present",
        "Cannot add a new tyre question because a question with a tyre element is already present in this checklist"
      );
  };

  const handleClose = () => {
    setSearchTerm("");
    setSelectedTagId(undefined);
    onClose();
  };

  const handleQuestionSelect = (question: Question) => {
    const addQuestionError = getAddQuestionError(question);

    if (addQuestionError) toast.error(addQuestionError);
    else onSelectQuestion(question);

    handleClose();
  };

  return (
    <Modal className="AddQuestionModal" open={isOpen} onClose={handleClose} closeOnDimmerClick={false}>
      <Modal.Header>
        <div className="add-question-header">
          <span>{t("v8_available_questions", "Available questions")}</span>

          <div className="header-actions">
            <Input
              icon="search"
              className="questions-search"
              value={searchTerm}
              placeholder={t("v8_search_questions_by_title", "Search questions by title...")}
              onChange={(_, { value }) => setSearchTerm(value)}
            />
            <Dropdown
              selection
              clearable
              className="questions-tags"
              placeholder={t("v8_question_tags", "Question tags")}
              options={tagsOptions}
              value={selectedTagId}
              onChange={setSelectedTagId}
            />
            <Button type="button" className="modal-close-btn" basic onClick={handleClose}>
              <Icon className="xmark grey" />
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Content scrolling>
        <Grid columns={2} divided>
          {!!dealerId && (
            <GridColumn width={isAdminUser ? 8 : 16}>
              <QuestionsColumn
                title={t("v8_dealer_questions", "Dealer Questions")}
                questions={dealerQuestions.filter(matchesFilters)}
                onQuestionSelect={handleQuestionSelect}
              />
            </GridColumn>
          )}

          {(!dealerId || isAdminUser) && (
            <GridColumn width={dealerId ? 8 : 16}>
              <QuestionsColumn
                title={t("v8_default_questions", "Default Questions")}
                questions={defaultQuestions.filter(matchesFilters)}
                onQuestionSelect={handleQuestionSelect}
              />
            </GridColumn>
          )}
        </Grid>
      </Modal.Content>
    </Modal>
  );
};
